import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, BookOpen, Users, Wrench, HeartHandshake } from "lucide-react";
import { useTopShopRegistration } from "@/components/topshop/useTopShopRegistration";
import { formatJamaicaTime } from "@/components/topshop/topShopRegistration";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import TopShopApplicationForm from "@/components/topshop/TopShopApplicationForm";
import TopShopStatusBar from "@/components/topshop/TopShopStatusBar";
import { useMyTopShopApplication } from "@/components/topshop/topShopShared";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import eabLogo from "@/assets/eab-logo-horizontal.png";
import shopIllustration from "@/assets/topshop/topshop-illustration.webp";
import shopShamrock from "@/assets/topshop/shop-shamrock.jpg";
import shopLovestreet from "@/assets/topshop/shop-lovestreet.jpg";
import shopBringy from "@/assets/topshop/shop-bringy.jpg";
import shopJudiths from "@/assets/topshop/shop-judiths.jpg";
import shopPatty from "@/assets/topshop/shop-patty.jpg";
import shopSmartChoice from "@/assets/topshop/shop-smart-choice.jpg";

const TOPSHOP_HOSTS = ["topshopchallenge.com", "www.topshopchallenge.com"];

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function TopShopLanding() {
  const { user, loading: authLoading } = useAuth();
  const isTopShopHost = TOPSHOP_HOSTS.includes(window.location.hostname);
  const isSignedIn = Boolean(user) && !isTopShopHost;
  const signInHref = isTopShopHost
    ? "https://eabconnect.com/auth?redirect=/topshop/application"
    : "/auth?redirect=/topshop/application";
  const registration = useTopShopRegistration();
  const applyLabel = registration.isOpen || registration.isLoading ? "Apply now" : "Join EAB Connect";
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: applicationData, isLoading: applicationLoading } = useMyTopShopApplication();
  const fallbackName = user?.email?.split("@")[0] || "there";
  const fullName = profile?.full_name?.trim() || fallbackName;
  const firstName = fullName.split(/\s+/)[0] || fallbackName;
  const application = isSignedIn ? applicationData?.application : undefined;
  const applicationStatus = application?.status as "submitted" | "accepted" | "waitlisted" | "declined" | "withdrawn" | "completed" | undefined;
  const photoCount = isSignedIn ? applicationData?.photos.length ?? 0 : 0;
  const signedInDataReady = !isSignedIn || (!profileLoading && !applicationLoading);
  const statusReady = !authLoading && signedInDataReady;
  const hasApplication = Boolean(application);
  const showPersonalHero = hasApplication && applicationStatus !== "declined";
  const heroLine = applicationStatus === "completed"
    ? `Congratulations ${firstName}, you completed the Top Shop Challenge. Keep learning and growing on EAB Connect.`
    : applicationStatus === "withdrawn"
    ? `Thank you for taking part, ${firstName}. You still have full access to EAB Connect training and mentors.`
    : applicationStatus === "accepted"
    ? "You're in. Watch your dashboard for your first challenge."
    : applicationStatus === "waitlisted"
      ? "You're on the waitlist. We'll let you know if a place opens up."
      : photoCount === 0
        ? "Your application is in. Add photos of your shop to finish it."
        : "Your application is under review. We'll be in touch about next steps.";
  const heroButtonLabel = applicationStatus === "submitted" && photoCount === 0 ? "Add photos" : "View application";

  const { data: focusAreas = [] } = useQuery({
    queryKey: ["top-shop", "focus-areas", "landing"],
    queryFn: async () => {
      const { data: program, error: programError } = await supabase
        .from("programs")
        .select("id")
        .eq("slug", "top-shop")
        .single();
      if (programError) throw programError;

      const { data, error } = await supabase
        .from("program_focus_areas")
        .select("id, name, sort_order")
        .eq("program_id", program.id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Top Shop Challenge | Powered by EAB Connect";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <div className="sticky top-0 z-40">
        <header className="border-b bg-background/95 backdrop-blur">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link to={isTopShopHost ? "/" : "/topshop"} aria-label="Top Shop Challenge home">
              <span className="flex items-center gap-3">
                <img src={eabLogo} alt="Entrepreneurs Across Borders" className="h-9 w-auto" />
                <span className="hidden items-center gap-3 border-l pl-3 leading-tight sm:flex">
                  <span className="text-sm font-bold text-foreground sm:text-base">Top Shop Challenge</span>
                  <span className="hidden text-xs text-muted-foreground sm:block">Powered by EAB Connect</span>
                </span>
              </span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              {statusReady && isSignedIn ? (
                <>
                  <Button asChild variant="ghost" className="h-10 gap-2 px-2 sm:px-3">
                    <Link to="/dashboard" aria-label="Go to your dashboard">
                      <UserAvatar avatarPath={profile?.avatar_url} fullName={fullName} className="h-8 w-8" thumbnailSize={48} />
                      <span className="max-w-24 truncate sm:max-w-40">{firstName}</span>
                    </Link>
                  </Button>
                  <Button asChild className="bg-warning text-foreground hover:bg-warning/90">
                    <Link to="/topshop/application">My application</Link>
                  </Button>
                </>
              ) : statusReady ? (
                <>
                  <Button asChild variant="outline" className="border-foreground/30 bg-background px-3 hover:bg-muted sm:px-4">
                    <a href={signInHref}>Sign in</a>
                  </Button>
                  <Button className="bg-warning text-foreground hover:bg-warning/90" onClick={() => scrollTo("apply")}>
                    <span className="sm:hidden">{applyLabel === "Join EAB Connect" ? "Join" : applyLabel}</span>
                    <span className="hidden sm:inline">{applyLabel}</span>
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        </header>
        <TopShopStatusBar
          isReady={statusReady}
          isSignedIn={isSignedIn}
          fullName={fullName}
          firstName={firstName}
          applicationStatus={applicationStatus}
          photoCount={photoCount}
          signInHref={signInHref}
          onApply={() => scrollTo("apply")}
          registrationClosed={!registration.isLoading && !registration.isOpen}
        />
      </div>

      <main>
        <section className="overflow-hidden bg-primary text-primary-foreground">
          <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 md:min-h-[680px] lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-20">
            <div className="max-w-3xl">
              <p className="mb-5 text-sm font-bold uppercase tracking-normal text-primary-foreground/80">{showPersonalHero ? "WELCOME BACK" : "Powered by EAB Connect from Entrepreneurs Across Borders"}</p>
              <h1 className="text-6xl font-extrabold leading-none tracking-normal sm:text-7xl lg:text-8xl">Top Shop</h1>
              <div className="mt-7 flex items-center gap-4">
                <span className="h-1 w-16 shrink-0 bg-warning" aria-hidden="true" />
                <p className="text-xl font-bold sm:text-2xl">Crown the Best. Build the Rest.</p>
              </div>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-primary-foreground/90 sm:text-xl">
                {showPersonalHero ? `Hi ${firstName}. ${heroLine}` : "A five month recovery and growth challenge for 100 small shops, stalls and vendors in western Jamaica. November 2026 to April 2027."}
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                {showPersonalHero ? (
                  <Button size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
                    <Link to="/topshop/application">{heroButtonLabel} <ArrowRight aria-hidden="true" /></Link>
                  </Button>
                ) : (
                  <Button size="lg" className="bg-warning text-warning-foreground hover:bg-warning/90" onClick={() => scrollTo("apply")}>
                    {applyLabel} <ArrowRight aria-hidden="true" />
                  </Button>
                )}
                <Button size="lg" variant="outline" className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary" onClick={() => scrollTo("how")}>
                  How it works <ArrowDown aria-hidden="true" />
                </Button>
              </div>
              {registration.isOpen && registration.closeAt && (
                <p className="mt-4 text-sm font-medium text-primary-foreground/90">Applications close on {formatJamaicaTime(registration.closeAt, false)} Jamaica time</p>
              )}
            </div>
            <img src={shopIllustration} alt="Illustration of a Jamaican top shop" className="mx-auto w-full max-w-2xl object-contain" />
          </div>
        </section>

        <section className="py-20 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:px-8">
            <div>
              <p className="font-bold text-warning">Why Top Shop</p>
              <h2 className="mt-3 text-3xl font-bold tracking-normal sm:text-4xl">Rebuilding the shops our communities depend on</h2>
              <p className="mt-6 text-lg leading-8 text-muted-foreground">
                In October 2025 Hurricane Melissa tore through western Jamaica and hit small shops, stalls and vendors hardest. These businesses keep neighborhoods running, but most are rebuilding without the systems, marketing know-how or capital to recover quickly on their own. Top Shop gives them a structured path back to stability and growth.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <img src={shopShamrock} alt="A local grocery shop in Jamaica" className="col-span-2 aspect-[16/8] w-full rounded-md object-cover" />
              <img src={shopLovestreet} alt="A brightly painted local grocery shop in Jamaica" className="aspect-[4/3] w-full rounded-md object-cover" />
              <img src={shopBringy} alt="A neighborhood wholesale shop in Jamaica" className="aspect-[4/3] w-full rounded-md object-cover" />
            </div>
          </div>
        </section>

        <section id="how" className="scroll-mt-20 bg-secondary py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="font-bold text-warning">How it works</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-normal sm:text-4xl">Five months. Three steps. One stronger business.</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                ["01", "Apply", "Tell us about your shop. Every applicant gets a free EAB Connect account with training, tools and mentors."],
                ["02", "Build", "Accepted shops work through seven areas of business growth with coaching and hands on support."],
                ["03", "Compete", "Shops compete on behalf of their community. Top performers win prizes and direct support to a local school, health center or community center, and are celebrated at the Jamaica Microbusiness Development Summit in Montego Bay in April 2027."],
              ].map(([number, title, body]) => (
                <article key={number} className="rounded-md border bg-card p-7 shadow-sm">
                  <span className="text-4xl font-extrabold text-primary/25">{number}</span>
                  <h3 className="mt-5 text-2xl font-bold">{title}</h3>
                  <p className="mt-3 leading-7 text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="eab-connect" className="scroll-mt-20 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="font-bold text-warning">Powered by EAB Connect</p>
            <h2 className="mt-3 max-w-3xl text-3xl font-bold tracking-normal sm:text-4xl">Top Shop lasts five months. EAB Connect stays with you for years.</h2>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-muted-foreground">Every shop that applies gets a free EAB Connect account. EAB Connect is the online community from Entrepreneurs Across Borders where small business owners learn, find mentors and grow. The challenge ends in April 2027, but your account, your learning and your connections stay with you whatever the outcome.</p>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {([
                [BookOpen, "Training at your own pace", "Short practical courses on running and growing a business, with certificates when you finish."],
                [Users, "Mentors who have done it", "Get advice from experienced business owners and mentors in the EAB community."],
                [Wrench, "Tools that save you time", "Build a pitch deck, find resources and keep track of your progress in one place."],
                [HeartHandshake, "A community that lasts", "Connect with other shop owners, join events and discussions, and keep growing after the challenge ends."],
              ] as const).map(([Icon, title, body]) => (
                <article key={title} className="rounded-md border bg-card p-7 shadow-sm">
                  <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
                  <h3 className="mt-5 text-xl font-bold">{title}</h3>
                  <p className="mt-3 leading-7 text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
            <Button size="lg" variant="outline" className="mt-10" onClick={() => scrollTo("how")}>
              How it works <ArrowDown aria-hidden="true" />
            </Button>
          </div>
        </section>

        <section className="bg-secondary py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="font-bold text-warning">The challenge</p>
            <h2 className="mt-3 text-3xl font-bold tracking-normal sm:text-4xl">Seven areas of growth</h2>
            <div className="mt-10 grid gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-2 lg:grid-cols-4">
              {focusAreas.map((area, index) => (
                <div key={area.id} className="min-h-36 bg-background p-6">
                  <span className="text-sm font-bold text-primary">{String(index + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 text-lg font-bold">{area.name}</h3>
                </div>
              ))}
              {/* Invisible filler keeps the empty grid slot white at the 2 and 4 column widths */}
              {focusAreas.length === 7 && (
                <div aria-hidden="true" className="hidden bg-background sm:block" />
              )}
            </div>
          </div>
        </section>

        <section aria-label="Jamaican neighborhood shops" className="grid sm:grid-cols-3">
          {[shopJudiths, shopPatty, shopSmartChoice].map((src, index) => (
            <img key={src} src={src} alt={["A small community shop in Jamaica", "A neighborhood food stall in Jamaica", "A local grocery business in Jamaica"][index]} className="aspect-[4/3] h-full w-full object-cover" />
          ))}
        </section>

        <section className="bg-primary py-16 text-primary-foreground">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-normal sm:text-4xl">Who's eligible</h2>
            <div className="mt-8 grid gap-8 text-left md:grid-cols-2">
              <div>
                <h3 className="text-xl font-bold">Who qualifies</h3>
                <p className="mt-3 text-lg leading-8 text-primary-foreground/90">We are accepting retail shops: shops that sell goods, such as general goods shops, grocery and corner shops, cook shops, stalls and vendors.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold">Who doesn't</h3>
                <p className="mt-3 text-lg leading-8 text-primary-foreground/90">Service based businesses do not qualify, for example barbers, hair and beauty salons, cosmetology, and similar services.</p>
              </div>
            </div>
            <p className="mx-auto mt-8 max-w-4xl text-lg leading-8 text-primary-foreground/90">
              Shops in Westmoreland, Hanover, St. Elizabeth, St. James and Trelawny can apply. Anyone in these five parishes is welcome to apply, but not every applicant will be selected for the Competition.
            </p>
          </div>
        </section>

        <section id="apply" className="scroll-mt-16 bg-secondary py-20 sm:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <TopShopApplicationForm />
          </div>
        </section>
      </main>

      <footer className="border-t bg-background py-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 text-sm text-muted-foreground sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <p>The Top Shop Challenge is a program of Entrepreneurs Across Borders and runs on EAB Connect.</p>
          <div className="flex gap-5">
            <a href="https://eaborders.org" target="_blank" rel="noreferrer" className="font-medium text-foreground hover:text-primary">eaborders.org</a>
            <a href="https://eabconnect.com" target="_blank" rel="noreferrer" className="font-medium text-foreground hover:text-primary">eabconnect.com</a>
            <Link to="/privacy-policy" className="font-medium text-foreground hover:text-primary">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}